import { Subject, Observable, interval, throttle, debounceTime, distinctUntilChanged } from 'rxjs';
import { filter, map } from 'rxjs/operators';

export interface EventConfig {
  throttleTime?: number;
  debounceTime?: number;
  distinctUntilChanged?: boolean;
}

export interface MouseEventData {
  type: 'mousedown' | 'mouseup' | 'mousemove' | 'click' | 'dblclick';
  clientX: number;
  clientY: number;
  button: number;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
  timestamp: number;
}

export interface TouchEventData {
  type: 'touchstart' | 'touchend' | 'touchmove';
  touches: Array<{
    clientX: number;
    clientY: number;
    identifier: number;
  }>;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
  timestamp: number;
}

export interface KeyboardEventData {
  type: 'keydown' | 'keyup' | 'keypress';
  key: string;
  code: string;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
  metaKey: boolean;
  repeat: boolean;
  timestamp: number;
}

export class EventManager {
  private _mouseEvents$ = new Subject<MouseEventData>();
  private _touchEvents$ = new Subject<TouchEventData>();
  private _keyboardEvents$ = new Subject<KeyboardEventData>();
  private _customEvents$ = new Subject<{ type: string; data: any }>();

  private _mouseEventFrame: number = 30;
  private _touchEventFrame: number = 30;
  private _keyboardEventFrame: number = 30;

  // 스로틀된 이벤트 스트림들
  private _throttledMouseMove$: Observable<MouseEventData>;
  private _throttledTouchMove$: Observable<TouchEventData>;

  // 이벤트 상태
  private _isMouseDown: boolean = false;
  private _isTouchInProgress: boolean = false;
  private _lastEventTime: number = 0;

  constructor() {
    // 마우스 이벤트 스로틀링 설정
    this._throttledMouseMove$ = this._mouseEvents$.pipe(
      throttle(() => interval(this._mouseEventFrame)),
    );

    // 터치 이벤트 스로틀링 설정
    this._throttledTouchMove$ = this._touchEvents$.pipe(
      throttle(() => interval(this._touchEventFrame)),
    );
  }

  /**
   * 마우스 이벤트 프레임 설정
   */
  public setMouseEventFrame(frame: number): void {
    this._mouseEventFrame = frame;
    this._throttledMouseMove$ = this._mouseEvents$.pipe(
      throttle(() => interval(this._mouseEventFrame)),
    );
  }

  /**
   * 터치 이벤트 프레임 설정
   */
  public setTouchEventFrame(frame: number): void {
    this._touchEventFrame = frame;
    this._throttledTouchMove$ = this._touchEvents$.pipe(
      throttle(() => interval(this._touchEventFrame)),
    );
  }

  /**
   * 키보드 이벤트 프레임 설정
   */
  public setKeyboardEventFrame(frame: number): void {
    this._keyboardEventFrame = frame;
  }

  /**
   * 마우스 이벤트 발생
   */
  public emitMouseEvent(event: MouseEvent): void {
    const eventData: MouseEventData = {
      type: event.type as any,
      clientX: event.clientX,
      clientY: event.clientY,
      button: event.button,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
      timestamp: Date.now(),
    };

    this._mouseEvents$.next(eventData);
    this._lastEventTime = eventData.timestamp;

    // 마우스 상태 업데이트
    if (event.type === 'mousedown') {
      this._isMouseDown = true;
    } else if (event.type === 'mouseup') {
      this._isMouseDown = false;
    }
  }

  /**
   * 터치 이벤트 발생
   */
  public emitTouchEvent(event: TouchEvent): void {
    const eventData: TouchEventData = {
      type: event.type as any,
      touches: Array.from(event.touches).map((touch) => ({
        clientX: touch.clientX,
        clientY: touch.clientY,
        identifier: touch.identifier,
      })),
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
      timestamp: Date.now(),
    };

    this._touchEvents$.next(eventData);
    this._lastEventTime = eventData.timestamp;

    // 터치 상태 업데이트
    if (event.type === 'touchstart') {
      this._isTouchInProgress = true;
    } else if (event.type === 'touchend') {
      this._isTouchInProgress = false;
    }
  }

  /**
   * 키보드 이벤트 발생
   */
  public emitKeyboardEvent(event: KeyboardEvent): void {
    const eventData: KeyboardEventData = {
      type: event.type as any,
      key: event.key,
      code: event.code,
      ctrlKey: event.ctrlKey,
      shiftKey: event.shiftKey,
      altKey: event.altKey,
      metaKey: event.metaKey,
      repeat: event.repeat,
      timestamp: Date.now(),
    };

    this._keyboardEvents$.next(eventData);
    this._lastEventTime = eventData.timestamp;
  }

  /**
   * 커스텀 이벤트 발생
   */
  public emitCustomEvent(type: string, data: any): void {
    this._customEvents$.next({ type, data });
    this._lastEventTime = Date.now();
  }

  /**
   * 마우스 이벤트 구독
   */
  public onMouseEvent(): Observable<MouseEventData> {
    return this._mouseEvents$.asObservable();
  }

  /**
   * 스로틀된 마우스 이동 이벤트 구독
   */
  public onThrottledMouseMove(): Observable<MouseEventData> {
    return this._throttledMouseMove$;
  }

  /**
   * 터치 이벤트 구독
   */
  public onTouchEvent(): Observable<TouchEventData> {
    return this._touchEvents$.asObservable();
  }

  /**
   * 스로틀된 터치 이동 이벤트 구독
   */
  public onThrottledTouchMove(): Observable<TouchEventData> {
    return this._throttledTouchMove$;
  }

  /**
   * 키보드 이벤트 구독
   */
  public onKeyboardEvent(): Observable<KeyboardEventData> {
    return this._keyboardEvents$.asObservable();
  }

  /**
   * 커스텀 이벤트 구독
   */
  public onCustomEvent(): Observable<{ type: string; data: any }> {
    return this._customEvents$.asObservable();
  }

  /**
   * 특정 타입의 커스텀 이벤트 구독
   */
  public onCustomEventType(type: string): Observable<any> {
    return this._customEvents$.pipe(
      filter((event) => event.type === type),
      map((event) => event.data),
    );
  }

  /**
   * 마우스 클릭 이벤트 구독
   */
  public onClick(): Observable<MouseEventData> {
    return this._mouseEvents$.pipe(filter((event) => event.type === 'click'));
  }

  /**
   * 마우스 더블클릭 이벤트 구독
   */
  public onDoubleClick(): Observable<MouseEventData> {
    return this._mouseEvents$.pipe(filter((event) => event.type === 'dblclick'));
  }

  /**
   * 마우스 다운 이벤트 구독
   */
  public onMouseDown(): Observable<MouseEventData> {
    return this._mouseEvents$.pipe(filter((event) => event.type === 'mousedown'));
  }

  /**
   * 마우스 업 이벤트 구독
   */
  public onMouseUp(): Observable<MouseEventData> {
    return this._mouseEvents$.pipe(filter((event) => event.type === 'mouseup'));
  }

  /**
   * 마우스 이동 이벤트 구독 (스로틀링 없음)
   */
  public onMouseMove(): Observable<MouseEventData> {
    return this._mouseEvents$.pipe(filter((event) => event.type === 'mousemove'));
  }

  /**
   * 터치 시작 이벤트 구독
   */
  public onTouchStart(): Observable<TouchEventData> {
    return this._touchEvents$.pipe(filter((event) => event.type === 'touchstart'));
  }

  /**
   * 터치 종료 이벤트 구독
   */
  public onTouchEnd(): Observable<TouchEventData> {
    return this._touchEvents$.pipe(filter((event) => event.type === 'touchend'));
  }

  /**
   * 터치 이동 이벤트 구독 (스로틀링 없음)
   */
  public onTouchMove(): Observable<TouchEventData> {
    return this._touchEvents$.pipe(filter((event) => event.type === 'touchmove'));
  }

  /**
   * 키 다운 이벤트 구독
   */
  public onKeyDown(): Observable<KeyboardEventData> {
    return this._keyboardEvents$.pipe(filter((event) => event.type === 'keydown'));
  }

  /**
   * 키 업 이벤트 구독
   */
  public onKeyUp(): Observable<KeyboardEventData> {
    return this._keyboardEvents$.pipe(filter((event) => event.type === 'keyup'));
  }

  /**
   * 특정 키 이벤트 구독
   */
  public onKey(key: string): Observable<KeyboardEventData> {
    return this._keyboardEvents$.pipe(filter((event) => event.key === key));
  }

  /**
   * 특정 키 코드 이벤트 구독
   */
  public onKeyCode(code: string): Observable<KeyboardEventData> {
    return this._keyboardEvents$.pipe(filter((event) => event.code === code));
  }

  /**
   * 이벤트 상태 확인
   */
  public isMouseDown(): boolean {
    return this._isMouseDown;
  }

  public isTouchInProgress(): boolean {
    return this._isTouchInProgress;
  }

  public getLastEventTime(): number {
    return this._lastEventTime;
  }

  /**
   * 이벤트 스트림 정리
   */
  public clearEventStreams(): void {
    this._mouseEvents$.complete();
    this._touchEvents$.complete();
    this._keyboardEvents$.complete();
    this._customEvents$.complete();
  }

  /**
   * 리소스 정리
   */
  public dispose(): void {
    this.clearEventStreams();
    this._isMouseDown = false;
    this._isTouchInProgress = false;
    this._lastEventTime = 0;
  }
}
